import React, { useState, useEffect, useCallback } from "react";
import { api, API, tokenParam } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import { expiryStatus, STATUS_STYLE, alertCount } from "../lib/haccp";
import { Plus, Trash2, Pencil, ShieldAlert, X, Loader2, Search, AlertTriangle, Mail, Recycle } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ product: "", ingredients: "", allergens: [], expiry: "", lot: "", storage: "", photo_file_id: null });

export default function Scadenze() {
  const { canWrite } = useAuth();
  const { config } = useConfig();
  const warn = config?.haccp_warn_days ?? 7;
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => api.get("/haccp").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const sendAlerts = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/haccp/alerts/send");
      if (data.count) toast.success(`Riepilogo inviato a ${data.sent_to} (${data.count} prodotti)`);
      else toast.info(data.message || "Nessun prodotto in scadenza da segnalare");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio non riuscito");
    } finally { setSending(false); }
  };

  const save = async () => {
    if (!form.product.trim()) return toast.error("Inserisci il nome del prodotto");
    setSaving(true);
    try {
      await api.post("/haccp", form);
      toast.success("Prodotto salvato nel registro");
      setForm(null);
      load();
    } catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo prodotto dal registro?")) return;
    await api.delete(`/haccp/${id}`);
    load();
  };

  const toWasteB = async (i) => {
    try {
      await api.post("/marpol/inventory/adjust", { waste_category: "B_food_waste", delta_m3: 0.02, storage_location: "Da prodotto scaduto" });
      toast.success(`"${i.product || "Prodotto"}" registrato nei rifiuti MARPOL (cat. B)`);
    } catch { toast.error("Registrazione rifiuto non riuscita"); }
  };

  const enriched = list
    .map((i) => ({ ...i, status: expiryStatus(i.expiry, warn) }))
    .sort((a, b) => {
      const order = { expired: 0, soon: 1, ok: 2, none: 3 };
      if (order[a.status.key] !== order[b.status.key]) return order[a.status.key] - order[b.status.key];
      return (a.status.days ?? 9999) - (b.status.days ?? 9999);
    });

  const filtered = enriched.filter((i) => {
    if (q && !`${i.product} ${i.lot} ${(i.allergens || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === "all") return true;
    return i.status.key === filter;
  });

  const nExpired = enriched.filter((i) => i.status.key === "expired").length;
  const nSoon = enriched.filter((i) => i.status.key === "soon").length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Registro Scadenze HACCP</h1>
          <p className="text-slate-500">Etichette scansionate e prodotti tracciati con avvisi di scadenza</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button data-testid="send-haccp-alerts-btn" onClick={sendAlerts} disabled={sending} className="flex h-14 items-center gap-2 rounded-xl border-2 border-gold px-5 font-semibold text-navy hover:bg-gold/10 disabled:opacity-60">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />} Invia riepilogo
            </button>
            <button data-testid="add-haccp-btn" onClick={() => setForm(empty())} className="flex h-14 items-center gap-2 rounded-xl bg-navy px-5 font-semibold text-white hover:bg-navy-hover">
              <Plus className="h-5 w-5" /> Aggiungi
            </button>
          </div>
        )}
      </div>

      {(nExpired > 0 || nSoon > 0) && (
        <div data-testid="haccp-alert-banner" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            {nExpired > 0 && <span className="font-semibold">{nExpired} prodotto/i scaduto/i. </span>}
            {nSoon > 0 && <span>{nSoon} in scadenza entro 7 giorni.</span>}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input data-testid="search-haccp" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto, lotto, allergene…" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
        </div>
        {[["all", "Tutti"], ["expired", "Scaduti"], ["soon", "In scadenza"], ["ok", "OK"]].map(([k, l]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`h-12 rounded-lg px-4 text-sm font-semibold ${filter === k ? "bg-navy text-white" : "bg-white text-navy border border-slate-200"}`}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-400">Nessun prodotto nel registro. Scansiona un'etichetta HACCP o aggiungi manualmente.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((i) => (
            <div key={i.id} data-testid={`haccp-${i.id}`} className="flex flex-col rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="font-head text-lg font-semibold text-navy">{i.product || "Senza nome"}</div>
                <span data-testid={`haccp-status-${i.id}`} className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[i.status.key]}`}>{i.status.label}</span>
              </div>
              {i.photo_file_id && (
                <img
                  data-testid={`haccp-photo-${i.id}`}
                  src={`${API}/files/by-id/${i.photo_file_id}?auth=${tokenParam()}`}
                  alt="etichetta"
                  className="mt-3 h-36 w-full rounded-lg border border-slate-200 object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              {i.expiry && <div className="tnum mt-1 text-sm text-slate-500">Scadenza: {i.expiry.split("-").reverse().join("/")}</div>}
              {i.lot && <div className="text-sm text-slate-500">Lotto: {i.lot}</div>}
              {i.allergens?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {i.allergens.map((a, idx) => <span key={idx} className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-600">{a}</span>)}
                </div>
              )}
              {i.storage && <div className="mt-2 text-xs text-slate-400">{i.storage}</div>}
              {canWrite && (
                <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
                  <button onClick={() => setForm(i)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-slate-100 py-2 text-sm font-medium"><Pencil className="h-4 w-4" /> Modifica</button>
                  {(i.status.key === "expired" || i.status.key === "soon") && (
                    <button data-testid={`haccp-waste-${i.id}`} title="Registra come rifiuto B (MARPOL)" onClick={() => toWasteB(i)} className="flex items-center justify-center gap-1 rounded-lg bg-emerald-50 px-3 text-emerald-700"><Recycle className="h-4 w-4" /></button>
                  )}
                  <button data-testid={`delete-haccp-${i.id}`} onClick={() => remove(i.id)} className="flex items-center justify-center rounded-lg bg-red-50 px-4 text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="animate-slide-up max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-6 sm:rounded-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-head text-xl font-bold text-navy">{form.id ? "Modifica" : "Nuovo"} prodotto</h3>
              <button onClick={() => setForm(null)}><X className="h-6 w-6 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <F label="Prodotto *" value={form.product} onChange={(v) => setForm({ ...form, product: v })} testid="haccp-form-product" />
              <F label="Ingredienti" value={form.ingredients} onChange={(v) => setForm({ ...form, ingredients: v })} testid="haccp-form-ingredients" />
              <F label="Allergeni (separati da virgola)" value={(form.allergens || []).join(", ")} onChange={(v) => setForm({ ...form, allergens: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="haccp-form-allergens" />
              <div className="grid grid-cols-2 gap-3">
                <F label="Scadenza" type="date" value={form.expiry} onChange={(v) => setForm({ ...form, expiry: v })} testid="haccp-form-expiry" />
                <F label="Lotto" value={form.lot} onChange={(v) => setForm({ ...form, lot: v })} testid="haccp-form-lot" />
              </div>
              <F label="Conservazione" value={form.storage} onChange={(v) => setForm({ ...form, storage: v })} testid="haccp-form-storage" />
            </div>
            <button data-testid="save-haccp-form-btn" onClick={save} disabled={saving} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-gold font-bold text-black hover:bg-gold-hover disabled:opacity-60">
              {saving && <Loader2 className="h-5 w-5 animate-spin" />} Salva nel registro
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-navy">{label}</label>
      <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
    </div>
  );
}
